'use client'

import { useQueryClient } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { Loader2Icon, SearchIcon, XIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect, useMemo, useRef, useState } from 'react'

import { WidgetEmptyState } from '@/components/widget/widget-empty-state'
import { useOpenServerResearchThread } from '@/features/order/research/use-open-research-thread'
import { useDebounce } from '@/hooks/use-debounce'
import { cn } from '@/lib/utils'
import {
  RESEARCH_THREAD_SEARCH_MAX_LENGTH,
  type ResearchThreadSummary,
} from '@/service/research.api'
import { researchKeys, useResearchThreads } from '@/service/research.hook'
import {
  activeThreadIdAtomFamily,
  ASSISTANT_SURFACE_ID,
  researchConversationAtomFamily,
  serverThreadIndexAtom,
} from '@/state/chat'
import { type ResearchJob, researchJobsAtom } from '@/state/research'

const RUNNING_PHASES = new Set<ResearchJob['phase']>(['STARTING', 'PENDING', 'RUNNING'])
const DAY_MS = 24 * 60 * 60 * 1000
const SKELETON_WIDTHS = ['w-4/5', 'w-3/5', 'w-2/3', 'w-1/2', 'w-3/4']

interface ThreadStatus {
  running: boolean
  unread: boolean
}

type DateGroup = 'groupToday' | 'groupYesterday' | 'groupWeek' | 'groupMonth' | 'groupOlder'

function dateGroup(at: number, now: Date): DateGroup {
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  if (at >= startOfToday) return 'groupToday'
  if (at >= startOfToday - DAY_MS) return 'groupYesterday'
  if (at >= startOfToday - 7 * DAY_MS) return 'groupWeek'
  if (at >= startOfToday - 30 * DAY_MS) return 'groupMonth'
  return 'groupOlder'
}

// Hover is a plain color change (seen tens of times a day: no motion); press darkens one step
// further so the click registers before the thread swaps in.
const ROW_CLASS =
  'text-foreground/70 hover:bg-foreground/5 hover:text-foreground active:bg-foreground/10 flex h-8 w-full cursor-pointer items-center gap-2 px-3 text-left text-xs transition-colors duration-150'

function ResearchRow({
  thread,
  status,
  active,
}: {
  thread: ResearchThreadSummary
  status?: ThreadStatus
  active: boolean
}) {
  const t = useTranslations('trade.assistant')
  const openServerThread = useOpenServerResearchThread()
  const title = thread.title || t('untitled')

  return (
    <button
      type="button"
      onClick={() => openServerThread(thread)}
      title={title}
      aria-current={active ? 'true' : undefined}
      className={cn(ROW_CLASS, active && 'bg-emphasis text-emphasis-foreground')}
    >
      <span className="min-w-0 flex-1 truncate">{title}</span>
      {status?.running ? (
        <Loader2Icon
          className="size-3 shrink-0 animate-spin opacity-60"
          aria-label={t('researchRunning')}
        />
      ) : status?.unread ? (
        <span className="bg-ox-cyan size-1.5 shrink-0 rounded-full" aria-label={t('unread')} />
      ) : null}
    </button>
  )
}

/** Row-shaped placeholders, so the first page lands without the list jumping. */
function RecentListSkeleton() {
  return (
    <div aria-hidden="true">
      {SKELETON_WIDTHS.map((width, index) => (
        <div key={index} className="flex h-8 items-center px-3">
          <div className={cn('bg-foreground/10 h-2.5 animate-pulse', width)} />
        </div>
      ))}
    </div>
  )
}

/** True while a research report is waiting to be read — drives the sidebar Assistant item's dot. */
export function useHasUnreadResearch() {
  const jobs = useAtomValue(researchJobsAtom)
  return useMemo(
    () => Object.values(jobs).some((job) => job.phase === 'DELIVERED' && !job.viewedAt),
    [jobs]
  )
}

/**
 * Everything the user has asked the assistant, newest activity first — one row per server
 * thread (GET /research/threads), grouped by day and searched by title on the server.
 * Running/unread markers come from this session's jobs on the matching local thread. Opening a
 * row shows it in the assistant.
 */
export function AssistantRecentList({ className }: { className?: string }) {
  const t = useTranslations('trade.assistant')
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const { value: debouncedSearch } = useDebounce(search.trim(), 250)
  const query = useResearchThreads(debouncedSearch)
  const jobs = useAtomValue(researchJobsAtom)
  const serverThreadIndex = useAtomValue(serverThreadIndexAtom)
  const activeThreadId = useAtomValue(activeThreadIdAtomFamily(ASSISTANT_SURFACE_ID))
  const activeServerThreadId = useAtomValue(
    researchConversationAtomFamily(activeThreadId)
  ).serverThreadId

  const groups = useMemo(() => {
    const seen = new Set<string>()
    const now = new Date()
    const result: { label: DateGroup; threads: ResearchThreadSummary[] }[] = []
    for (const thread of (query.data?.pages ?? []).flatMap((page) => page.threads)) {
      if (seen.has(thread.threadId)) continue
      seen.add(thread.threadId)
      const label = dateGroup(thread.updatedAt, now)
      const last = result[result.length - 1]
      if (last?.label === label) last.threads.push(thread)
      else result.push({ label, threads: [thread] })
    }
    return result
  }, [query.data])

  const statusByServerThread = useMemo(() => {
    const byLocal = new Map<string, ThreadStatus>()
    for (const job of Object.values(jobs)) {
      const current = byLocal.get(job.threadId) ?? { running: false, unread: false }
      byLocal.set(job.threadId, {
        running: current.running || RUNNING_PHASES.has(job.phase),
        unread: current.unread || (job.phase === 'DELIVERED' && !job.viewedAt),
      })
    }
    const byServer = new Map<string, ThreadStatus>()
    for (const [serverThreadId, threadId] of serverThreadIndex) {
      const status = byLocal.get(threadId)
      if (status) byServer.set(serverThreadId, status)
    }
    return byServer
  }, [jobs, serverThreadIndex])

  // A new conversation, or a run settling, changes the server's list (new row, new title,
  // reordered by activity): refetch it then rather than polling.
  const refreshKey = useMemo(
    () =>
      [
        [...serverThreadIndex.keys()].sort().join(','),
        Object.values(jobs).filter((job) => !RUNNING_PHASES.has(job.phase)).length,
      ].join('|'),
    [jobs, serverThreadIndex]
  )
  const lastRefreshKey = useRef(refreshKey)
  useEffect(() => {
    if (lastRefreshKey.current === refreshKey) return
    lastRefreshKey.current = refreshKey
    void queryClient.invalidateQueries({ queryKey: researchKeys.threads() })
  }, [queryClient, refreshKey])

  // Starts the next page a row or two before the end is visible, so scrolling rarely waits.
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = query
  const sentinelRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel || !hasNextPage) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) void fetchNextPage()
      },
      { rootMargin: '0px 0px 96px 0px' }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const hasRows = groups.length > 0
  // A new search keeps the previous rows (placeholderData) and dims them until results land,
  // instead of flashing the skeleton on every pause in typing.
  const isSearching = query.isPlaceholderData && query.isFetching
  const isFirstLoad = query.isPending
  const isError = query.isError && !hasRows
  const isEmpty = !isFirstLoad && !isError && !hasRows

  return (
    <div className={cn('flex min-h-0 flex-col', className)} data-tutorial="history">
      <label className="border-foreground/10 focus-within:border-foreground/30 mx-3 mb-1 flex h-8 shrink-0 items-center gap-2 border px-2 transition-colors duration-150">
        <SearchIcon className="size-3.5 shrink-0 opacity-50" aria-hidden="true" />
        <input
          aria-label={t('search')}
          value={search}
          maxLength={RESEARCH_THREAD_SEARCH_MAX_LENGTH}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape' && search) {
              event.stopPropagation()
              setSearch('')
            }
          }}
          placeholder={t('search')}
          className="min-w-0 flex-1 bg-transparent text-xs outline-none placeholder:opacity-50"
        />
        {search ? (
          <button
            type="button"
            onClick={() => setSearch('')}
            aria-label={t('clearSearch')}
            className="text-foreground/40 hover:text-foreground -mr-1 shrink-0 p-1 transition-colors duration-150"
          >
            <XIcon className="size-3" />
          </button>
        ) : null}
      </label>
      <div
        className={cn(
          'min-h-0 flex-1 overflow-y-auto mask-b-from-[calc(100%-24px)] pb-6 transition-opacity duration-150',
          isSearching && 'opacity-50'
        )}
        aria-busy={isFirstLoad || isSearching || isFetchingNextPage}
      >
        {groups.map((group) => (
          <section key={group.label} aria-label={t(group.label)}>
            <p className="text-foreground/40 px-3 pt-3 pb-1 text-[11px]">{t(group.label)}</p>
            {group.threads.map((thread) => (
              <ResearchRow
                key={thread.threadId}
                thread={thread}
                status={statusByServerThread.get(thread.threadId)}
                active={!!activeServerThreadId && thread.threadId === activeServerThreadId}
              />
            ))}
          </section>
        ))}
        <div ref={sentinelRef} aria-hidden="true" />
        {isFirstLoad ? <RecentListSkeleton /> : null}
        {isFetchingNextPage ? (
          <div className="flex h-8 items-center justify-center" aria-label={t('loading')}>
            <Loader2Icon className="size-3 animate-spin opacity-40" />
          </div>
        ) : null}
        {isError ? (
          <WidgetEmptyState className="h-auto py-2">
            <span>{t('loadFailed')}</span>{' '}
            <button
              type="button"
              onClick={() => void query.refetch()}
              className="text-foreground/70 hover:text-foreground underline underline-offset-2"
            >
              {t('retry')}
            </button>
          </WidgetEmptyState>
        ) : null}
        {isEmpty ? (
          <WidgetEmptyState className="h-auto py-2">
            {debouncedSearch ? t('noMatches') : t('empty')}
          </WidgetEmptyState>
        ) : null}
      </div>
    </div>
  )
}
